// Small SWR-style in-memory cache for GET calls with a 30s TTL.
// - Serves cached data instantly on revisit if fresh.
// - Deduplicates concurrent in-flight requests (single fetch shared).
// - Auto-aborts on unmount via signal option pass-through.
import { api } from "@/lib/api";

const DEFAULT_TTL_MS = 30_000;
const cache = new Map();     // key → { data, ts }
const inflight = new Map();  // key → Promise

function keyOf(url, params) {
  const sortedParams = params
    ? Object.keys(params).sort().map(k => `${k}=${JSON.stringify(params[k])}`).join("&")
    : "";
  return `${url}?${sortedParams}`;
}

export async function cachedGet(url, { params, ttl = DEFAULT_TTL_MS, force = false, signal } = {}) {
  const k = keyOf(url, params);
  const now = Date.now();
  const hit = cache.get(k);
  if (!force && hit && now - hit.ts < ttl) return hit.data;

  if (!force && inflight.has(k)) return inflight.get(k);

  const p = api.get(url, { params, signal })
    .then(r => {
      cache.set(k, { data: r.data, ts: Date.now() });
      inflight.delete(k);
      return r.data;
    })
    .catch(err => {
      inflight.delete(k);
      throw err;
    });
  inflight.set(k, p);
  return p;
}

/** Invalidate every cache entry whose URL starts with `prefix`. */
export function invalidate(prefix) {
  for (const k of Array.from(cache.keys())) {
    if (k.startsWith(prefix)) cache.delete(k);
  }
}

export function clearCache() { cache.clear(); inflight.clear(); }
